/**
 * useTimelineZoom Hook
 *
 * Manages zoom level with center-anchored scroll preservation.
 * Handles initialization, bounds clamping, and auto-scroll to NOW line.
 *
 * Animation strategy:
 * - React state always holds the final target value (never fractional mid-animation).
 *   This keeps button targets stable and eliminates render cascade.
 * - Visual zoom animation is handled via CSS scaleX on the SVG elements (button clicks only).
 *   The browser compositor runs it — zero extra React renders during the transition.
 * - Slider drag: instant state update + center-anchored scroll (no animation needed;
 *   continuous input provides its own smooth feedback).
 * - Scroll is applied via useLayoutEffect so it commits in the same frame as the SVG resize,
 *   preventing the size/scroll mismatch that causes jiggling.
 */

import { useEffect, useCallback, useMemo, useRef, useLayoutEffect } from 'react'
import { WEEK_CONTAINER_FALLBACK_WIDTH } from '../timeline-constants'

const CSS_TRANSITION_DURATION = 80 // ms

/**
 * useTimelineZoom - Manage timeline zoom level
 *
 * @param {Object} params
 * @param {number} params.weekContainerWidth - Current zoom level (pixels per week)
 * @param {number} params.totalWeeks - Number of weeks in timeline
 * @param {number} params.containerWidth - Container width in pixels
 * @param {boolean} params.autoFitZoom - Auto-fit timeline to container width
 * @param {Object} params.contentRef - Content scroll container ref
 * @param {Object} params.headerRef - Header scroll container ref
 * @param {number} params.NOW_X - Current date X position
 * @param {Function} params.setWeekContainerWidth - Stable zoom setter
 * @param {number} params.scrollToX - Optional custom scroll target X position (default: NOW_X)
 *
 * @returns {{ minWeekWidth, maxWeekWidth, handleZoomChange }}
 */
export const useTimelineZoom = ({
  weekContainerWidth,
  totalWeeks,
  containerWidth,
  autoFitZoom,
  contentRef,
  headerRef,
  NOW_X,
  setWeekContainerWidth,
  scrollToLeftOffset,
  scrollToX = null,
  scrollTrigger = 0,
  isDraggingHandleRef = null,
}) => {

  // Tracks the latest value written to state — avoids stale closure reads in handleZoomChange
  const currentWeekWidthRef = useRef(weekContainerWidth)
  // Scroll to apply after React commits the SVG at its new size
  const targetScrollRef = useRef(null)
  // CSS transition params for button-click animation (null = no animation)
  const cssTransitionRef = useRef(null) // { startScale, centerX }
  const cleanupTimeoutRef = useRef(null)

  // Keep ref in sync when state changes from outside (init, clamp, external updates)
  useEffect(() => {
    currentWeekWidthRef.current = weekContainerWidth
  }, [weekContainerWidth])

  // Synchronise scroll + apply optional CSS scale animation after React commits the SVG.
  // useLayoutEffect fires before the browser paints, so size and scroll land in the same frame.
  useLayoutEffect(() => {
    if (targetScrollRef.current !== null && contentRef.current) {
      contentRef.current.scrollLeft = targetScrollRef.current
      if (headerRef.current) headerRef.current.scrollLeft = targetScrollRef.current
      targetScrollRef.current = null
    }

    if (cssTransitionRef.current !== null && contentRef.current) {
      const { startScale, centerX } = cssTransitionRef.current
      cssTransitionRef.current = null

      if (cleanupTimeoutRef.current) {
        clearTimeout(cleanupTimeoutRef.current)
        cleanupTimeoutRef.current = null
      }

      const contentSvg = contentRef.current.querySelector('svg')
      const headerSvg = headerRef.current?.querySelector('svg')

      const applyTransition = (el) => {
        if (!el) return
        // Set starting state with no transition, then force a layout read so the
        // browser registers it, then enable the transition to the identity scale.
        el.style.transition = 'none'
        el.style.transform = `scaleX(${startScale})`
        el.style.transformOrigin = `${centerX}px 0px`
        el.getBoundingClientRect() // force style recalc so transition starts from here
        el.style.transition = `transform ${CSS_TRANSITION_DURATION}ms ease-out`
        el.style.transform = 'scaleX(1)'
      }

      applyTransition(contentSvg)
      applyTransition(headerSvg)

      cleanupTimeoutRef.current = setTimeout(() => {
        const cleanup = (el) => {
          if (!el) return
          el.style.removeProperty('transition')
          el.style.removeProperty('transform')
          el.style.removeProperty('transform-origin')
        }
        cleanup(contentSvg)
        cleanup(headerSvg)
        cleanupTimeoutRef.current = null
      }, CSS_TRANSITION_DURATION + 20)
    }
  }, [weekContainerWidth, contentRef, headerRef])

  // Calculate minimum zoom (fit entire timeline in container)
  const minWeekWidth = useMemo(() => {
    if (!containerWidth || containerWidth === 0) return 8
    return Math.max(8, containerWidth / totalWeeks)
  }, [containerWidth, totalWeeks])

  // Maximum zoom (user-defined limit)
  const maxWeekWidth = 64

  // Initialize and clamp zoom level
  useEffect(() => {
    setWeekContainerWidth(prev => {
      if (prev === null && containerWidth > 0) {
        const initialWidth = autoFitZoom ? minWeekWidth : WEEK_CONTAINER_FALLBACK_WIDTH
        return Math.max(minWeekWidth, Math.min(maxWeekWidth, initialWidth))
      }
      if (prev !== null) {
        return Math.max(minWeekWidth, Math.min(maxWeekWidth, prev))
      }
      return prev
    })
  }, [containerWidth, autoFitZoom, minWeekWidth, maxWeekWidth, setWeekContainerWidth])

  const handleZoomChange = useCallback((_e, newValue) => {
    const isButtonClick = _e === null
    const startWeekWidth = currentWeekWidthRef.current
    if (!startWeekWidth || startWeekWidth === newValue) return

    // Center-anchored scroll: keep the viewport's center content-point fixed after resize
    const initialScrollLeft = contentRef.current?.scrollLeft ?? 0
    const initialCenterX = initialScrollLeft + containerWidth / 2
    const initialTimeline = totalWeeks * startWeekWidth
    const centerRatio = initialTimeline > 0 ? initialCenterX / initialTimeline : 0
    const newTimeline = totalWeeks * newValue
    const newScrollLeft = Math.max(0, centerRatio * newTimeline - containerWidth / 2)

    // Button clicks get a CSS scaleX animation (visual only — state is already the final value).
    // Slider drag gets no animation: the continuous input itself provides smooth feedback.
    if (isButtonClick) {
      const centerX = newScrollLeft + containerWidth / 2
      cssTransitionRef.current = { startScale: startWeekWidth / newValue, centerX }
    }

    currentWeekWidthRef.current = newValue
    targetScrollRef.current = newScrollLeft
    setWeekContainerWidth(newValue)
  }, [containerWidth, totalWeeks, contentRef, setWeekContainerWidth])

  // Auto-scroll to target position on mount and when scrollTrigger fires
  useEffect(() => {
    if (isDraggingHandleRef?.current) return

    const targetX = scrollToX !== null ? scrollToX : NOW_X
    const targetScroll = Math.max(0, targetX - (scrollToLeftOffset || containerWidth / 2))

    if (headerRef.current && contentRef.current) {
      headerRef.current.scrollLeft = targetScroll
      contentRef.current.scrollLeft = targetScroll
    }
  }, [NOW_X, scrollToX, containerWidth, scrollToLeftOffset, scrollTrigger, headerRef, contentRef, isDraggingHandleRef])

  return {
    minWeekWidth,
    maxWeekWidth,
    handleZoomChange,
  }
}
